import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router";
import {
  Home,
  Newspaper,
  BarChart3,
  PanelLeft,
  PanelLeftClose,
  Settings,
  Menu,
  LogOut,
  User,
  Puzzle,
  GripVertical,
  Moon,
  Sun,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { Avatar } from "../ui/avatar";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useTheme } from "../../contexts/theme-context";
import { useAuthContext } from "../../contexts/auth-context";
import { WorkspaceSwitcher } from "../workspace-switcher";
import { OrganizationMenu } from "../organization-menu";

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  section?: string;
}

const navItems: NavItem[] = [
  { title: "My work", href: "/my-work", icon: <Home className="h-4 w-4" />, section: "Personal" },
  { title: "Digest", href: "/digest", icon: <Newspaper className="h-4 w-4" />, section: "Personal" },
  { title: "Productivity", href: "/productivity", icon: <BarChart3 className="h-4 w-4" />, section: "Personal" },
];

const MIN_WIDTH = 200;
const MAX_WIDTH = 320;
const DEFAULT_WIDTH = 220;

export function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const { user: currentUser, loading: userLoading, signOut } = useAuthContext();
  const { theme, toggleTheme } = useTheme();

  const isActive = (href: string) => location.pathname === href;

  const handleSignOut = () => {
    void signOut();
  };

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className={cn("flex items-center px-3 py-4", isCollapsed ? "justify-center" : "justify-between")}>
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <img
              src={theme === "dark" ? "/logo-light.svg" : "/logo.svg"}
              alt="Omni AI Logo"
              className="h-7 w-7"
            />
            <span className="font-brand text-foreground">Omni AI</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden md:flex h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          {isCollapsed ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-2">
        {/* Personal Section */}
        <div className="mb-3">
          {!isCollapsed && (
            <div className="px-3 py-1 text-small text-tertiary font-medium">
              Personal
            </div>
          )}
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-2 rounded-component px-3 py-1.5 transition-smooth",
                  isActive(item.href)
                    ? "bg-border text-foreground font-medium"
                    : "text-muted-foreground hover:bg-hover hover:text-foreground",
                  isCollapsed && "justify-center"
                )}
                title={isCollapsed ? item.title : undefined}
              >
                {item.icon}
                {!isCollapsed && <span>{item.title}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Workspaces Section */}
        <WorkspaceSwitcher isCollapsed={isCollapsed} />
      </nav>

      <Separator />

      {/* Organization and account */}
      <div className="space-y-1 p-2">
        <OrganizationMenu isCollapsed={isCollapsed} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex min-h-11 w-full items-center gap-2 rounded-component px-2 py-1.5 hover:bg-hover transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
                isCollapsed && "justify-center px-1"
              )}
              disabled={userLoading}
              aria-label="Account menu"
            >
              <Avatar
                src={currentUser?.imageUrl || ""}
                alt={currentUser?.fullName || currentUser?.username || ""}
                className="h-7 w-7"
              />
              {!isCollapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {userLoading ? "Loading..." : (currentUser?.fullName || currentUser?.username || "User")}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {currentUser?.email || "Account"}
                  </p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium text-foreground">
                {userLoading ? "Loading..." : (currentUser?.fullName || currentUser?.username || "User")}
              </p>
              <p className="text-xs text-muted-foreground">{currentUser?.email || ""}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/profile" className="min-h-11">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/settings" className="min-h-11">
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/integrations" className="min-h-11">
                <Puzzle className="mr-2 h-4 w-4" />
                <span>Integrations</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme} className="min-h-11 cursor-pointer">
              {theme === "light" ? (
                <Moon className="mr-2 h-4 w-4" />
              ) : (
                <Sun className="mr-2 h-4 w-4" />
              )}
              <span>{theme === "light" ? "Dark mode" : "Light mode"}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="min-h-11 cursor-pointer text-danger">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "fixed top-4 z-[60] md:hidden",
          isMobileOpen ? "left-[12.25rem]" : "left-4",
        )}
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label={isMobileOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={isMobileOpen}
        aria-controls="mobile-sidebar"
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-foreground/50 z-40"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Desktop sidebar */}
      <aside
        ref={sidebarRef}
        className={cn(
          "hidden md:flex flex-col bg-sidebar border-r border-border transition-all duration-300 relative",
          isCollapsed ? "w-16" : ""
        )}
        style={{ width: isCollapsed ? undefined : `${sidebarWidth}px` }}
      >
        {sidebarContent}

        {/* Resize handle */}
        {!isCollapsed && (
          <div
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize group hover:bg-border transition-colors"
            onMouseDown={startResizing}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        )}
      </aside>

      {/* Mobile sidebar */}
      {isMobileOpen && (
        <aside
          id="mobile-sidebar"
          aria-label="Primary navigation"
          className="fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-border bg-sidebar md:hidden"
        >
          {sidebarContent}
        </aside>
      )}
    </>
  );
}
